import { useState, useEffect } from 'react';
import {
  Button,
  ButtonSize,
  ButtonStyles,
  Theme,
  GenericLink,
  useBreakpoint,
  ButtonIconPosition,
  Breakpoints,
  ButtonWidth,
  NextgenImage,
  LoaderType,
} from '@nextgen-web-framework/all';
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';
import PortalModal from '../portal/Portal';
import { getGradientClass } from '../../utils/themeColor';
import VideoModal from './components/VideoModal';
import { Gradient } from './types/propsTypes';
import type VideoBannerProps from './types/propsTypes';
import styles from './VideoBanner.module.scss';

const VideoBanner = (videoBannerProps: VideoBannerProps) => {
  const viewPortType = useBreakpoint();
  const [videoLoading, setVideoLoading] = useState(true);
  const [modalState, setModalState] = useState(false);
  const frontImage = videoBannerProps.images?.find((item) => item.viewport.includes(viewPortType));
  const brandLogo = videoBannerProps.brandLogo?.find((item) => item.viewport.includes(viewPortType));
  const gradientColor = videoBannerProps.linearGradient
    ? getGradientClass(videoBannerProps.linearGradient)
    : videoBannerProps.overlayGradient
    ? Gradient[videoBannerProps.overlayGradient]
    : '';
  const isMobile = [Breakpoints.SM, Breakpoints.XS].includes(viewPortType as Breakpoints);
  const [isSSR, setIsSSR] = useState(true);

  useEffect(() => {
    setIsSSR(false);
  }, []);
  const handleVideoLoaded = () => setVideoLoading(false);

  const toggleModal = () => {
    setModalState(!modalState);
  };

  return (
    <div className={styles['video-banner-container']} data-testid="video-banner-testid">
      {!videoBannerProps.backgroundVideoUrl && frontImage ? (
        <NextgenImage
          src={frontImage.url}
          alt={frontImage.altText}
          className={styles['video-banner-bg-image']}
          loading="eager"
          loaderType={LoaderType.CONTENTSTACK}
        />
      ) : (
        ''
      )}
      <div className={styles['video-player-container']}>
        {videoLoading && (
          <div className={styles['video-banner-skeleton-wrapper']}>
            <SkeletonLoader isRoundCorner={false} />
          </div>
        )}
        {isSSR && videoBannerProps.backgroundVideoUrl ? (
          ''
        ) : (
          <video
            className={styles['video-player']}
            style={videoLoading ? { visibility: 'hidden' } : {}}
            autoPlay
            loop
            muted
            playsInline
            id="video-banner-id"
            onLoadedData={handleVideoLoaded}
          >
            <source src={videoBannerProps.backgroundVideoUrl} />
          </video>
        )}
      </div>
      <div className={`${styles['video-banner-bottom-content-container']} ${styles[gradientColor]}`}>
        <div className={styles['video-banner-details']}>
          {brandLogo?.url && (
            <div className={styles['video-banner-logo-container']} data-testid="video-banner-logo-container-testid">
              <NextgenImage
                className={styles['video-banner-logo']}
                src={brandLogo?.url ?? ''}
                alt={brandLogo?.altText ?? ''}
              />
            </div>
          )}
          {videoBannerProps?.brandTagLine && (
            <h6 className={styles['video-banner-title']}>{videoBannerProps?.brandTagLine}</h6>
          )}
        </div>
        <div className={styles['video-banner-footer-button']}>
          {videoBannerProps.videoModalData.videoSection ? (
            <Button
              title={videoBannerProps.watchVideoText}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.WHITE}
              size={ButtonSize.MEDIUM}
              icon={{ iconName: 'play' }}
              iconPosition={ButtonIconPosition.LEFT}
              onClick={toggleModal}
              data-testid="video-banner-watch-button-testid"
              buttonWidth={isMobile ? ButtonWidth.FULL_WIDTH : ButtonWidth.FIT_CONTENT}
            />
          ) : (
            ''
          )}
          <GenericLink
            target={videoBannerProps?.shopNow?.target}
            href={videoBannerProps?.shopNow?.url}
            forceNative={false}
          >
            <Button
              title={videoBannerProps?.shopNow?.text}
              buttonStyle={ButtonStyles.SOLID}
              theme={Theme.WHITE}
              size={ButtonSize.MEDIUM}
              data-testid="video-banner-shop-button-testid"
              buttonWidth={isMobile ? ButtonWidth.FULL_WIDTH : ButtonWidth.FIT_CONTENT}
            />
          </GenericLink>
        </div>
      </div>
      {modalState ? (
        <PortalModal elementId="react-portal-modal" customStyles={false}>
          <VideoModal onClose={toggleModal} videoSection={videoBannerProps.videoModalData.videoSection} />
        </PortalModal>
      ) : (
        ''
      )}
    </div>
  );
};

export default VideoBanner;
export { VideoBannerProps };
